/**
 * 個別説明 (briefing) の型定義。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §3.5, §3.6, §7
 *
 * Phase 1: BriefingSchedule (公開フォームの候補日ピッカーで動的取得用)
 * Phase 5: BriefingRecord (個別説明実施後の記録)
 */
import type { IsoOrTimestamp } from './common';
import type { LineDiscountType } from './quote';

/** 開催スタイル。`both` は両方可 (オンライン/現地どちらでも申込可能)。 */
export type BriefingScheduleStyle = 'online' | 'onsite' | 'both';

/**
 * 実際に予約・確定されるスタイル (2026-08-10 追加)。
 * `both` の枠は公開フォームで「オンライン」「現地」の2候補に分割表示されるため、
 * 申込・確定の時点では必ず online / onsite のどちらかに確定する (`both` は残らない)。
 */
export type BriefingBookingStyle = 'online' | 'onsite';

/** 開催スタイルの表示ラベル。 */
export const BRIEFING_STYLE_LABEL: Record<BriefingScheduleStyle, string> = {
  online: 'オンライン',
  onsite: '現地',
  both: '両方可',
};

/**
 * 候補日の枠が削除済み (スケジュールタブの削除ボタン = deleteBriefingSchedule で消えた) のときの表記
 * (2026-10-13)。管理画面の候補日表示で、枠 id の代わりに使う。
 */
export const BRIEFING_DELETED_SLOT_LABEL = '削除された候補日';

/**
 * 申込者向けの受付メール (完了通知) で、削除済みの枠の代わりに出す表記 (2026-10-13)。
 * 「削除」は申込者には不具合のように読めるため、選べなくなったことだけを伝える文言にする。
 * 枠の記録が無いため、選ばれた日時そのものは出せない。
 */
export const BRIEFING_CLOSED_SLOT_MAIL_LABEL = '受付終了となった日程';

/**
 * `both` 枠を分割表示する際に、選択値へスタイルを埋め込むための区切り文字。
 * 公開フォームの回答値は `both` 枠のみ `"<scheduleId>::online"` 形式になり、
 * online / onsite 単独枠は従来どおり枠 id そのまま (後方互換)。
 */
export const BRIEFING_SLOT_STYLE_SEP = '::';

/** 枠 id と選択スタイルから、フォーム回答値 (選択肢の value) を組み立てる。 */
export function encodeBriefingSlotValue(
  scheduleId: string,
  style?: BriefingBookingStyle | null,
): string {
  return style ? `${scheduleId}${BRIEFING_SLOT_STYLE_SEP}${style}` : scheduleId;
}

/**
 * フォーム回答値を枠 id と選択スタイルに分解する。
 * スタイル無し (旧データ / 単独スタイル枠) は style=null を返す。
 */
export function parseBriefingSlotValue(raw: string): {
  scheduleId: string;
  style: BriefingBookingStyle | null;
} {
  const idx = raw.lastIndexOf(BRIEFING_SLOT_STYLE_SEP);
  if (idx <= 0) return { scheduleId: raw, style: null };
  const suffix = raw.slice(idx + BRIEFING_SLOT_STYLE_SEP.length);
  if (suffix !== 'online' && suffix !== 'onsite') {
    return { scheduleId: raw, style: null };
  }
  return { scheduleId: raw.slice(0, idx), style: suffix };
}

/**
 * 表示・記録用の実開催スタイルを解決する。
 * 枠が `both` かつ選択スタイルがある場合はそれを採用し、それ以外は枠のスタイル。
 * (選択スタイル未保存の旧データのみ `both` が残る)
 */
export function resolveBriefingStyle(
  slotStyle: BriefingScheduleStyle | null | undefined,
  selectedStyle?: BriefingBookingStyle | null,
): BriefingScheduleStyle {
  if (slotStyle === 'both' && selectedStyle) return selectedStyle;
  return slotStyle ?? 'online';
}

/**
 * 1つの枠を公開フォームの選択肢へ展開する。
 * `both` 枠は「オンライン」「現地」の2件に分割、それ以外は1件。
 * styleFilter (フォーム個別の参加スタイル指定) が online / onsite の場合はその1件のみ。
 */
export function expandBriefingSlotStyles(
  slotStyle: BriefingScheduleStyle,
  styleFilter?: BriefingScheduleStyle | null,
): Array<{ style: BriefingBookingStyle; splitFromBoth: boolean }> {
  const variants: BriefingBookingStyle[] =
    slotStyle === 'both' ? ['online', 'onsite'] : [slotStyle];
  const filtered =
    styleFilter && styleFilter !== 'both' ? variants.filter((v) => v === styleFilter) : variants;
  return filtered.map((style) => ({ style, splitFromBoth: slotStyle === 'both' }));
}

/**
 * 個別説明可能枠。
 * 担当者が「カレンダーで日付クリック → 時間設定 → 保存」した枠。
 * 公開フォームの `briefing_schedule_picker` フィールドで一覧取得され、
 * `bookedCount === 0` (未予約) のものだけが候補に出る (1枠=1予約)。
 */
export interface BriefingSchedule {
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm (24h)
  endTime: string; // HH:mm
  style: BriefingScheduleStyle;
  zoomUrl?: string | null;
  zoomId?: string | null;
  /** 現地の場合の場所 (style=onsite/both 時) */
  location?: string | null;
  /** 入力者がそのまま担当者として割り当てられる */
  assigneeUid: string;
  /** 受入人数 = 1組の同時参加可能人数 (参加人数入力の上限)。空き判定には使わない。 */
  capacity: number;
  /** 予約件数 (0=空き / 1以上=予約済みでクローズ)。1枠=1予約 (2026-07-23 確定)。 */
  bookedCount: number;
  isActive: boolean;
  createdAt: IsoOrTimestamp;
}

// ─────────────────────────────────────────────
// 個別説明記録 (Phase 5+: deals/{dealId}/briefing_records/{recordId})
// ─────────────────────────────────────────────

/** 成約レベル (1=低 〜 5=高)。営業担当者の主観評価。 */
export type ConversionLevel = 1 | 2 | 3 | 4 | 5;

export type PaymentMethod = 'transfer' | 'credit_card' | 'undecided';
export type Installments = 12 | 24 | 36 | 48 | 60;
export type TradeInType = 'discount' | 'cashback';

/**
 * 検討商品アイテム（1 つの製品行）。
 *  - master 参照時: productId に products コレクションの id、productName は選択時点のスナップショット
 *  - 自由記述時:    productId = null、productName に手入力テキスト
 */
export interface ConsideredProductItem {
  productId: string | null;
  productName: string;
  quantity: number;
  /** 行割引タイプ (¥固定額 / %)。見積作成時に引き継がれる (2026-07-08 追加) */
  discountType?: LineDiscountType | null;
  /** 割引値 (amount: 税抜円 / percent: 0〜100)。null = 割引なし */
  discountValue?: number | null;
}

/** 検討グループ（複数製品の組み合わせ案） */
export interface ConsiderationGroup {
  /** 任意ラベル（例: 「メイン案」「予算重視案」）。未設定可 */
  label?: string | null;
  items: ConsideredProductItem[];
}

export interface BriefingRecord {
  /**
   * 紐付く briefing_schedules の id (確定した枠)。
   * 2026-05-28: schedule なしの手動追加に対応するため null 許容に拡張。
   * null = 商談画面から直接「記録を手動追加」で作成された記録。
   */
  briefingScheduleId: string | null;
  conductedAt: IsoOrTimestamp;
  /** 担当者 uid */
  conductedBy: string;
  /** 成約レベル (1〜5) */
  conversionLevel: ConversionLevel;
  /** 関心のある製品 (products_list の products[] 内の sku または modelName)。
   *  Phase A 以降は consideredProducts に置き換え（後方互換のため残置） */
  interestedProductIds: string[];
  /** 検討商品（複数案 × 複数製品）。Phase A 以降の入力 */
  consideredProducts?: ConsiderationGroup[];
  // 顧客の声
  inquiries: string; // 知りたいこと/気になっていること
  introductionImage: string; // 導入イメージ
  concerns: string; // 導入への不安や悩み
  // 購入意向
  purchaseTimeline: string;
  paymentMethod: PaymentMethod;
  installments?: Installments | null;
  hasTradeIn: boolean;
  tradeInType?: TradeInType | null;
  // セール・特典
  saleAware: boolean;
  saleName?: string | null;
  hasIncentive: boolean;
  incentiveName?: string | null;
  // フォローアップ
  nextAction: string;
  supplementaryNotes: string;
  /**
   * v3.2.17: 個別説明代行料が含まれた SA 請求書 ID。
   * - null: 未請求
   * - 値: SA がこの briefing を含めて請求書を発行済み
   * - SA 請求書取消時は null に戻る
   * - 通常の admin / editor が記録した場合は null のまま（代行料発生対象は SA のみ）
   */
  commissionInvoiceId?: string | null;
  /**
   * 記録の状態 (2026-07-24 追加、2026-08-07 not_conducted 追加)。
   *  - 'draft':         下書き保存。完了扱いにならず (完了タブ/ボードバッジ対象外)、
   *                     SA 代行料の請求対象からも除外。実施担当者が後から編集して完了できる。
   *  - 'completed':     個別説明終了 (確定)。undefined は後方互換で completed 扱い。
   *  - 'not_conducted': 未実施マーク (v3.4.1)。個別説明が実施されなかった確定申込を
   *                     クローズするための記録。SA 代行料の請求対象から除外。
   *                     markBriefingNotConducted で作成、undoBriefingNotConducted で取消 (削除)。
   */
  status?: 'draft' | 'completed' | 'not_conducted';
  createdAt: IsoOrTimestamp;
  /**
   * 記録の編集履歴 (2026-07-23 追加)。
   * 保存済み記録は実施担当者 (conductedBy) 本人のみ編集可能。
   */
  updatedAt?: IsoOrTimestamp | null;
  updatedBy?: string | null;
}
